import { useState } from "react";
import {
    Container,
    ContainerSize,
    Form,
    FormControlType,
    Heading,
    Text
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type RangePageProps = {}

const RangePage = ({ }: RangePageProps) => {
    const [rangeOut, setRangeOut] = useState("50");

    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Range
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">Form.Control</Text> with <Text el="code">FormControlType.Range</Text> for Bootstrap’s{' '}
                <Text el="code">.form-range</Text> styling—consistent track and thumb across browsers, as in the{' '}
                <a
                    href="https://getbootstrap.com/docs/5.3/forms/range/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap range docs
                </a>
                .
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Overview
            </Heading>
            <Text className="mb-4">
                Range inputs use <Text el="code">form-range</Text> (not <Text el="code">form-control</Text>) so the track
                and thumb match Bootstrap.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="customRange1">Example range</Form.Label>
                        <Form.Control
                            type={FormControlType.Range}
                            id="customRange1"
                        />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rangeOverviewCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Disabled
            </Heading>
            <Text className="mb-4">
                Set <Text el="code">disabled</Text> for a muted range that cannot be focused.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="disabledRange">Disabled range</Form.Label>
                        <Form.Control
                            type={FormControlType.Range}
                            id="disabledRange"
                            disabled
                        />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rangeDisabledCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Min and max
            </Heading>
            <Text className="mb-4">
                Defaults are <Text el="code">min=0</Text> and <Text el="code">max=100</Text>; override with{' '}
                <Text el="code">min</Text> and <Text el="code">max</Text> props.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="customRange2">Example range</Form.Label>
                        <Form.Control
                            type={FormControlType.Range}
                            id="customRange2"
                            min={0}
                            max={5}
                        />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rangeMinMaxCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Steps
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">step</Text> to change snapping (for example <Text el="code">0.5</Text> between 0 and 5).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="customRange3">Example range</Form.Label>
                        <Form.Control
                            type={FormControlType.Range}
                            id="customRange3"
                            min={0}
                            max={5}
                            step={0.5}
                        />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rangeStepsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Output value
            </Heading>
            <Text className="mb-4">
                Pair the range with an <Text el="code">&lt;output&gt;</Text> element and update it on{' '}
                <Text el="code">onInput</Text>, following Bootstrap’s pattern.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="customRange4">Example range</Form.Label>
                        <Form.Control
                            type={FormControlType.Range}
                            id="customRange4"
                            min={0}
                            max={100}
                            defaultValue={50}
                            onInput={(e) => setRangeOut((e.target as HTMLInputElement).value)}
                        />
                        <output htmlFor="customRange4" className="d-block mt-2" aria-live="polite">
                            {rangeOut}
                        </output>
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rangeOutputCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

const rangeOverviewCode = `<Form.Group>
  <Form.Label htmlFor="customRange1">Example range</Form.Label>
  <Form.Control
    type={FormControlType.Range}
    id="customRange1"
  />
</Form.Group>`;

const rangeDisabledCode = `<Form.Group>
  <Form.Label htmlFor="disabledRange">Disabled range</Form.Label>
  <Form.Control
    type={FormControlType.Range}
    id="disabledRange"
    disabled
  />
</Form.Group>`;

const rangeMinMaxCode = `<Form.Group>
  <Form.Label htmlFor="customRange2">Example range</Form.Label>
  <Form.Control
    type={FormControlType.Range}
    id="customRange2"
    min={0}
    max={5}
  />
</Form.Group>`;

const rangeStepsCode = `<Form.Group>
  <Form.Label htmlFor="customRange3">Example range</Form.Label>
  <Form.Control
    type={FormControlType.Range}
    id="customRange3"
    min={0}
    max={5}
    step={0.5}
  />
</Form.Group>`;

const rangeOutputCode = `const [rangeOut, setRangeOut] = useState("50");

<Form.Group>
  <Form.Label htmlFor="customRange4">Example range</Form.Label>
  <Form.Control
    type={FormControlType.Range}
    id="customRange4"
    min={0}
    max={100}
    defaultValue={50}
    onInput={(e) => setRangeOut((e.target as HTMLInputElement).value)}
  />
  <output htmlFor="customRange4" className="d-block mt-2" aria-live="polite">
    {rangeOut}
  </output>
</Form.Group>`;

export default RangePage;
